import { motion } from "framer-motion";
import GlowButton from "@/components/primitives/GlowButton";
import GridBackground from "@/components/primitives/GridBackground";
import HudFrame from "@/components/primitives/HudFrame";
import BlueprintBackdrop from "@/components/motion/BlueprintBackdrop";

export default function FinalCTA() {
    return (
        <section
            id="cta"
            data-testid="final-cta-section"
            className="relative bg-tca-deep overflow-hidden"
        >
            {/* Ambient blueprint imprint */}
            <BlueprintBackdrop
                src="/brand/blueprint-02.jpg"
                opacity={0.065}
                parallaxRange={110}
                rotateRange={1.2}
            />
            {/* Faint cyan grid above blueprint */}
            <div className="absolute inset-0 pointer-events-none opacity-50">
                <GridBackground fine vignette={false} />
            </div>

            {/* Twin glow accents */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse 60% 60% at 50% 70%, rgba(0,224,232,0.16), transparent 60%)",
                }}
            />

            <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-28 sm:py-40 flex flex-col items-center text-center">
                {/* Eyebrow */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="font-label text-tca-cyan/85 mb-6 sm:mb-8"
                    data-testid="cta-eyebrow"
                >
                    02 · Begin
                </motion.div>

                {/* Headline */}
                <motion.h2
                    initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="cta-headline"
                    className="font-display text-[52px] leading-[0.98] sm:text-[88px] sm:leading-[0.94] lg:text-[120px] lg:leading-[0.92] tracking-[-0.03em] text-tca-ink"
                >
                    READY TO <span className="italic neon-cyan font-light">BUILD?</span>
                </motion.h2>

                {/* Sub mono */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    data-testid="cta-mono-sub"
                    className="mt-8 sm:mt-10 font-mono-tight text-xs sm:text-sm uppercase tracking-[0.36em] text-tca-cyan/60"
                >
                    // stop guessing. start building.
                </motion.div>

                {/* Big CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, delay: 0.35 }}
                    className="mt-12 sm:mt-14"
                >
                    <GlowButton
                        size="lg"
                        as="a"
                        href="#access"
                        data-testid="cta-primary-button"
                    >
                        Begin Your System
                    </GlowButton>
                </motion.div>

                {/* HUD marker frame */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, delay: 0.55 }}
                    className="mt-16 sm:mt-20 w-full max-w-3xl"
                >
                    <HudFrame
                        bracketSize={16}
                        borderTint="rgba(0, 224, 232, 0.14)"
                        innerClassName="px-6 py-5 sm:px-10 sm:py-6 flex flex-col sm:flex-row items-center justify-between gap-4"
                    >
                        <div className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-tca-ink-muted/90">
                            Phase Status
                        </div>
                        <div className="font-mono-tight text-[12px] sm:text-sm tracking-[0.18em] text-tca-cyan">
                            A → H · DEPLOYABLE
                        </div>
                        <div className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-tca-ink-muted/60">
                            I → Z · Advanced
                        </div>
                    </HudFrame>
                </motion.div>
            </div>
        </section>
    );
}
